import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, WifiOff, Check, Plus, Trash2 } from 'lucide-react';
import { User } from '../types';
import { fetchBookings, Booking, isLifecycleConnected } from '../utils/lifecycleApi';

const DEFAULT_CHECKLIST = [
  { section: 'Equipment', items: ['Camera bodies (main + backup)', 'Lenses (standard, wide, telephoto)', 'Memory cards (formatted + extras)', 'Batteries (charged + spares)', 'Tripod / Monopod', 'Flash / Speedlights', 'Drone + remote + batteries', 'Drone ND filters', 'Audio recorder / lapel mic', 'Gimbal / Stabilizer'] },
  { section: 'Pre-Event', items: ['Venue visit done', 'Shot list prepared', 'Team briefing done', 'Customer POC confirmed', 'Event schedule received', 'Entry / access passes arranged', 'Parking arranged', 'Backup contractor confirmed (if any)'] },
  { section: 'On Event Day', items: ['Reached venue on time', 'Equipment setup complete', 'Sound check done', 'First look shot done', 'Family shots covered', 'Key moments list reviewed', 'Drone shots done', 'BTS captured'] },
  { section: 'Post Event', items: ['Raw footage backed up (2 copies)', 'Memory cards formatted', 'Footage handed to PP vendor', 'Client thanked', 'Invoice raised', 'PP vendor briefed on deliverables'] },
];

const STORAGE_KEY = 'ss_audit_checklist_v1';
function loadChecklists(): Record<number, Record<string, boolean>> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function saveChecklists(data: any) { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }

export default function AuditChecklist({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Booking | null>(null);
  const [checks, setChecks] = useState<Record<number, Record<string, boolean>>>(loadChecklists());
  const [customItems, setCustomItems] = useState<Record<number, string[]>>({});
  const [newItem, setNewItem] = useState('');

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try { setBookings(await fetchBookings()); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  const isChecked = (bookingId: number, item: string) => checks[bookingId]?.[item] || false;

  const toggle = (bookingId: number, item: string) => {
    const updated = { ...checks, [bookingId]: { ...(checks[bookingId] || {}), [item]: !isChecked(bookingId, item) } };
    setChecks(updated); saveChecklists(updated);
  };

  const allItems = (bookingId: number) => {
    const defaults = DEFAULT_CHECKLIST.flatMap(s => s.items);
    return [...defaults, ...(customItems[bookingId] || [])];
  };

  const progress = (bookingId: number) => {
    const all = allItems(bookingId);
    const done = all.filter(i => isChecked(bookingId, i)).length;
    return { done, total: all.length, pct: all.length ? Math.round(done / all.length * 100) : 0 };
  };

  const addCustom = () => {
    if (!selected || !newItem.trim()) return;
    const updated = { ...customItems, [selected.id]: [...(customItems[selected.id] || []), newItem.trim()] };
    setCustomItems(updated); setNewItem('');
  };

  if (!connected) return (
    <div className="page-content">
      <div className="alert alert-error"><WifiOff size={15} /> <span>Not connected to Lifecycle backend.</span></div>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Event Audit Checklist</h1><p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Pre-event and post-event checklist to ensure nothing is missed</p></div>
        <button className="btn btn-ghost btn-icon" onClick={load}><RefreshCw size={15} /></button>
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><AlertTriangle size={15} /> <span>{error}</span></div>}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>
        <div>
          <label style={{ fontSize: 12, marginBottom: 8 }}>Select Booking</label>
          {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : bookings.map(b => {
            const { done, total, pct } = progress(b.id);
            return (
              <div key={b.id} onClick={() => setSelected(b)} style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: `1.5px solid ${selected?.id === b.id ? 'var(--teal)' : 'var(--border)'}`, background: selected?.id === b.id ? 'rgba(56,178,172,0.06)' : '#fff' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{b.customer_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.csr_number} · {b.service_type}</div>
                <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'var(--bg-subtle)' }}>
                  <div style={{ height: '100%', borderRadius: 2, width: pct + '%', background: pct === 100 ? 'var(--teal)' : 'var(--gold)', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{done}/{total} done ({pct}%)</div>
              </div>
            );
          })}
        </div>

        <div>
          {!selected ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Select a booking to view checklist</div>
          ) : (
            <>
              {/* Progress bar */}
              {(() => { const { done, total, pct } = progress(selected.id); return (
                <div className="card" style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>{selected.customer_name} — {selected.csr_number}</span>
                    <span style={{ color: pct === 100 ? 'var(--teal)' : 'var(--gold)', fontWeight: 700 }}>{done}/{total} ({pct}%)</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-subtle)' }}>
                    <div style={{ height: '100%', borderRadius: 4, width: pct + '%', background: pct === 100 ? 'var(--teal)' : 'var(--gold)', transition: 'width 0.3s' }} />
                  </div>
                </div>
              ); })()}

              {/* Checklist by section */}
              {DEFAULT_CHECKLIST.map(section => (
                <div key={section.section} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', marginBottom: 12 }}>{section.section}</div>
                  {section.items.map(item => (
                    <div key={item} onClick={() => toggle(selected.id, item)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer', borderRadius: 4 }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isChecked(selected.id, item) ? 'var(--teal)' : 'var(--border)'}`, background: isChecked(selected.id, item) ? 'var(--teal)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {isChecked(selected.id, item) && <Check size={11} color="white" />}
                      </div>
                      <span style={{ fontSize: 13, color: isChecked(selected.id, item) ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: isChecked(selected.id, item) ? 'line-through' : 'none' }}>{item}</span>
                    </div>
                  ))}
                </div>
              ))}

              {/* Custom items */}
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', marginBottom: 12 }}>Custom Items</div>
                {(customItems[selected.id] || []).map(item => (
                  <div key={item} onClick={() => toggle(selected.id, item)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isChecked(selected.id, item) ? 'var(--teal)' : 'var(--border)'}`, background: isChecked(selected.id, item) ? 'var(--teal)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {isChecked(selected.id, item) && <Check size={11} color="white" />}
                    </div>
                    <span style={{ fontSize: 13, flex: 1 }}>{item}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input className="input" placeholder="Add custom checklist item" value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustom()} />
                  <button className="btn btn-secondary" onClick={addCustom}><Plus size={14} /></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
